import React, { useState, useEffect, useMemo } from 'react';
import NewsCard from '../components/news/NewsCard';
import SafeIcon from '../common/SafeIcon';
import supabase from '../supabase/supabase';

const News = () => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  
  // Pagination State
  const [visibleCount, setVisibleCount] = useState(10);

  useEffect(() => {
    const fetchNews = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('ai_news')
          .select('*')
          .eq('status', 'published')
          .order('created_at', { ascending: false });

        if (error) throw error;
        if (data) {
          setNews(data.map(item => ({
            id: item.id,
            category: item.category || 'General',
            hook: item.hook || item.title,
            summary: item.summary,
            points: item.points,
            whyItMatters: item.why_it_matters,
            source: item.source,
            date: new Date(item.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
            readTime: item.read_time,
            deepLinks: item.deep_links,
            ctaText: item.cta_text,
            ctaUrl: item.cta_url
          })));
        }
      } catch (err) {
        console.error("Error fetching news:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchNews();
  }, []);

  // Dynamically extract categories handling comma-separated values
  const categories = useMemo(() => {
    const allCats = news
      .flatMap(n => (n.category || 'General').split(',').map(c => c.trim()))
      .filter(Boolean);
    return [...new Set(allCats)].sort();
  }, [news]);

  const toggleCategory = (cat) => {
    setSelectedCategories(prev => 
      prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]
    );
    setVisibleCount(10); // Reset pagination on filter change
  };

  const filteredNews = useMemo(() => {
    if (selectedCategories.length === 0) return news;
    return news.filter(item => {
      const itemCats = (item.category || 'General').split(',').map(c => c.trim());
      return selectedCategories.some(sc => itemCats.includes(sc));
    });
  }, [news, selectedCategories]);

  const visibleNews = filteredNews.slice(0, visibleCount);

  return (
    <div className="w-full">
      {/* Header and Filter Section */}
      <div className="bg-brand-surface border-b border-brand-border pt-10 pb-8 px-4 md:px-6 lg:px-8">
        <div className="max-w-[800px] mx-auto">
          <div className="flex justify-between items-start md:items-center flex-col md:flex-row gap-4 mb-4">
            <h1 className="text-[28px] md:text-[36px] font-bold text-brand-text leading-tight">AI News</h1>
            
            <button 
              onClick={() => setIsFilterOpen(!isFilterOpen)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-[8px] text-[14px] font-bold transition-colors border ${
                isFilterOpen || selectedCategories.length > 0
                  ? 'bg-brand-mint/10 text-brand-mint border-brand-mint/30'
                  : 'bg-brand-bg text-brand-text border-brand-border hover:bg-brand-raised'
              }`}
            >
              <SafeIcon icon="Filter" className="w-4 h-4" />
              <span>{selectedCategories.length > 0 ? `Filters (${selectedCategories.length})` : 'Filter'}</span>
              <SafeIcon icon={isFilterOpen ? "ChevronUp" : "ChevronDown"} className="w-4 h-4 ml-1 hidden sm:block" />
            </button>
          </div>

          {/* Collapsible Category Panel */}
          {isFilterOpen && (
            <div className="pt-4 pb-2 border-t border-brand-border animate-in fade-in slide-in-from-top-2 duration-200">
              <p className="text-[11px] font-mono font-bold text-brand-muted uppercase tracking-widest mb-3">Select Categories</p>
              {categories.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {categories.map(cat => (
                    <label 
                      key={cat}
                      className="flex items-center gap-2 cursor-pointer group px-3 py-2 rounded-[8px] hover:bg-brand-bg border border-transparent hover:border-brand-border transition-colors select-none"
                    >
                      <input 
                        type="checkbox"
                        className="sr-only"
                        checked={selectedCategories.includes(cat)}
                        onChange={() => toggleCategory(cat)}
                      />
                      <div className={`w-4 h-4 rounded-[4px] border flex items-center justify-center transition-colors ${
                        selectedCategories.includes(cat) 
                          ? 'bg-brand-mint border-brand-mint' 
                          : 'border-brand-muted group-hover:border-brand-text'
                      }`}>
                        {selectedCategories.includes(cat) && <SafeIcon icon="Check" className="w-3 h-3 text-brand-bg" />}
                      </div>
                      <span className="text-[14px] text-brand-secondary group-hover:text-brand-text transition-colors">{cat}</span>
                    </label>
                  ))}
                  {selectedCategories.length > 0 && (
                    <button 
                      onClick={() => { setSelectedCategories([]); setVisibleCount(10); }}
                      className="px-3 py-2 text-[13px] font-medium text-brand-breaking hover:opacity-80 transition-opacity ml-2"
                    >
                      Clear All
                    </button>
                  )}
                </div>
              ) : (
                <p className="text-[13px] text-brand-secondary italic">No categories available yet.</p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Feed */}
      <div className="max-w-[800px] mx-auto px-4 md:px-6 pt-8 space-y-8 pb-12">
        {loading ? (
          <div className="flex justify-center py-20">
            <SafeIcon icon="Loader" className="w-8 h-8 text-brand-mint animate-spin" />
          </div>
        ) : filteredNews.length > 0 ? (
          <>
            {visibleNews.map(item => (
              <NewsCard key={item.id} news={item} />
            ))}
            
            <div className="text-center pt-8 border-t border-brand-border">
              {visibleCount < filteredNews.length ? (
                <button 
                  onClick={() => setVisibleCount(prev => prev + 10)}
                  className="px-6 py-2.5 rounded-[8px] bg-brand-surface border border-brand-border text-brand-text text-[14px] font-bold hover:bg-brand-raised transition-colors inline-block"
                >
                  Load More Stories
                </button>
              ) : (
                <p className="text-[14px] text-brand-secondary font-mono italic px-4">
                  That's all the intel we have. The AI hamsters need a break! 🐹💤
                </p>
              )}
            </div>
          </>
        ) : (
          <div className="text-center py-20">
            <h3 className="text-[18px] font-bold text-brand-text mb-2">No Stories Found</h3>
            <p className="text-brand-secondary text-[14px]">
              {selectedCategories.length > 0 
                ? "Try adjusting your category filters." 
                : "We are currently gathering the latest AI intel. Check back shortly."}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default News;